<template>
  <div class="mc-image-capture__wrapper">
    <template v-if="showTooltipLabelCamera">
      <span class="label-camera">
        <i class="icon-fill_image" />
        {{ $t('trackVideo.camera') + ' ' + camera }}
      </span>
      <div class="background-gradient"></div>
    </template>
    <Connecting v-if="isConnecting || $props.isConnectingRequest" />
    <b-spinner v-else-if="isGeneratingPreview" />
    <ImageShow
      v-else-if="mediaFilename && imageUrl"
      :key="`${mediaFilename}-${imageUrl}`"
      :image-url="imageUrl"
      :media-filename="mediaFilename"
    />
    <Uploading v-else-if="statusDvh === 'I'" />
    <NoImage v-else-if="!canRequestImage" />
    <NoImageV2 v-else-if="isNoImage" />
    <Request v-else :disabled="isMoreThan3Days" @clickRequestPhoto="clickRequestPhoto" />
  </div>
</template>
<script>
import ImageShow from './ImageShow.vue'
import NoImage from './NoImage.vue'
import NoImageV2 from './NoImageV2.vue'
import Connecting from './Connecting.vue'
import Request from './Request.vue'
import Uploading from './Uploading.vue'
import moment from 'moment'
import { GET_VEHICLE_STATUSES_LAST_RECEIVE } from '@/services/api/fleet'
import { Helper } from '@/mixin/helper'
import { SET_UPLOAD_MEDIA, UPDATE_FILELIST } from '@/services/api/onlineCommand'

moment.locale('id')

export default {
  components: { ImageShow, Connecting, Uploading, Request, NoImage, NoImageV2 },
  mixins: [Helper],
  props: {
    channel: { type: Number, required: true },
    camera: { type: Number, required: true },
    vehicleIdCam: { type: Number, required: true },
    imeiCam: { type: String, required: true },
    imageUrl: { type: String, required: false, default: '' },
    mediaFilename: { type: String, required: false, default: '' },
    statusDvh: { type: String, required: false, default: '' },
    rawAlertCreatedOn: { type: String, required: true },
    rawAlertId: { type: Number, required: true },
    isConnectingRequest: { type: Boolean, required: false },
    alertCode: { type: Number, required: true },
    canRequestImage: { type: Boolean, required: false, default: false },
    showTooltipLabelCamera: { type: Boolean, required: false, default: false },
    deviceType: { type: String, required: true },
    isGeneratingPreview: { type: Boolean, required: false, default: false },
    isDemo: { type: Boolean, required: false, default: false },
  },
  emits: ['startConnectingRequest', 'endConnectingRequest'],
  data() {
    return {
      isConnecting: false,
      noImageState: JSON.parse(localStorage.getItem('notification-no-image')) ?? [],
    }
  },
  computed: {
    isMoreThan3Days() {
      if (!this.rawAlertCreatedOn) return false
      return moment(this.rawAlertCreatedOn).isBefore(moment().subtract(3, 'days'))
    },
    isNoImage() {
      return this.noImageState.some(
        (item) => item.rawAlertId === this.rawAlertId && item.channel === this.channel
      )
    },
    isImageUploading() {
      return this.statusDvh === 'I'
    },
  },
  methods: {
    restoreMediaRequestState({ isOfflineDevice = false, markNoImage = false } = {}) {
      const hasExistingMedia = Boolean(this.imageUrl)

      if (markNoImage && !hasExistingMedia) {
        this.setNoImageStateToLocalStorage()
      }

      this.$store.dispatch('notification/setMediaPollingState', {
        id: this.rawAlertId,
        pollingStatus: hasExistingMedia ? 'complete' : 'timeout',
        canRequest: !hasExistingMedia,
        ...(hasExistingMedia ? {} : { videoData: null }),
        isOfflineDevice: hasExistingMedia ? false : isOfflineDevice,
      })
    },
    addListenerStartConnecting() {
      if (this.$listeners && !!this.$listeners['startConnectingRequest']) {
        this.$emit('startConnectingRequest', this.vehicleIdCam, this.channel)
      }
    },
    addListenerEndConnecting() {
      if (this.$listeners && !!this.$listeners['endConnectingRequest']) {
        this.$emit('endConnectingRequest', this.vehicleIdCam, this.channel)
      }
    },
    async checkDeviceOffline() {
      const lastReceive = await this.getLastReceive()
      const diffMinutes = this.getDurationBetween2Dates(lastReceive, this.now())
      return diffMinutes > 10
    },
    async getLastReceive() {
      const response = await this.$apollo.query({
        query: GET_VEHICLE_STATUSES_LAST_RECEIVE,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            vehicleId: [this.vehicleIdCam],
            vehicleStatus: ['service', 'archived', 'repair'],
          },
        },
      })
      return response.data.vehicleStatuses.vehicleStatuses?.[0]?.lastReceive
    },
    async clickRequestPhoto() {
      if (this?.isDemo) {
        this.$mcToast.warning(this.$t('notification.text.deviceUnreachable'))
        return
      }
      try {
        this.isConnecting = true
        this.addListenerStartConnecting()
        this.$store.dispatch('notification/setMediaPollingState', {
          id: this.rawAlertId,
          pollingStatus: 'requesting',
        })
        const isDeviceOffline = await this.checkDeviceOffline()

        if (isDeviceOffline) {
          this.$mcToast.warning(this.$t('notification.text.deviceUnreachable'))
          this.restoreMediaRequestState({ isOfflineDevice: true })
          this.addListenerEndConnecting()
          this.isConnecting = false
          return
        }

        const startTime = this.rawAlertCreatedOn
        const endTime =
          this.deviceType === 'vss-howen'
            ? moment(this.rawAlertCreatedOn).clone().add(2, 'seconds').toISOString()
            : moment(this.rawAlertCreatedOn).clone().add(1, 'seconds').toISOString()

        const input = {
          imei: this.imeiCam,
          rawAlertCode: this.alertCode,
          channel: this.channel,
          startTime,
          endTime,
          identifier: 'CaptureVideo',
          rawAlertCreatedOn: this.rawAlertCreatedOn,
          isEvent: true,
          useInMemoryDB: true,
        }

        await this.updateFileList()
        await this.delay(5000)

        const response = await this.$apollo.mutate({
          mutation: SET_UPLOAD_MEDIA,
          variables: {
            input,
          },
        })

        const responseMessage = response.data.setUploadMedia.setUploadMedia.message
        const dataDvh = response.data.setUploadMedia.setUploadMedia.data

        if (responseMessage === 'Not Sent(No video downloaded)') {
          this.$mcToast.error(this.$t('notification.text.photoNotFound'))
          this.restoreMediaRequestState({ markNoImage: true })
          this.isConnecting = false
          this.addListenerEndConnecting()
          return
        }
        if (response.data.setUploadMedia.isSuccessful && responseMessage === 'Sent') {
          dataDvh.forEach((data) => {
            this.$store.commit('notification/addDataDvhRequested', {
              ...data,
              rawAlertId: this.rawAlertId,
            })
          })
          this.$store.dispatch('notification/setMediaPollingState', {
            id: this.rawAlertId,
            pollingStatus: 'checking',
          })
          this.isConnecting = false
          this.addListenerEndConnecting()
        } else {
          throw new Error('setUploadMedia not successfull')
        }
      } catch (error) {
        console.error(error)
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        this.restoreMediaRequestState()
        this.isConnecting = false
        this.addListenerEndConnecting()
      }
    },
    async updateFileList() {
      const input = {
        imei: this.imeiCam,
        useInMemoryDB: true,
      }
      if (this.deviceType === 'bsj' || this.deviceType === 'vss-howen') {
        const dateRange = this.getDefaultUpdateFileListAlertDateRange(this.rawAlertCreatedOn)
        input['channel'] = this.channel
        input['dateRange'] = {
          start: dateRange.start,
          end: dateRange.end,
        }
      }
      await this.$apollo.mutate({
        mutation: UPDATE_FILELIST,
        variables: {
          input,
        },
        fetchPolicy: 'no-cache',
      })
    },
    setNoImageStateToLocalStorage() {
      const newArrNoImageState = [
        ...this.noImageState,
        { rawAlertId: this.rawAlertId, channel: this.channel },
      ]
      localStorage.setItem('notification-no-image', JSON.stringify(newArrNoImageState))
      this.noImageState = newArrNoImageState
      this.$emit('no-image', { rawAlertId: this.rawAlertId, channel: this.channel })
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
